import type { Component } from "solid-js";
import { createEffect, createMemo, createSignal, For, on, onCleanup, onMount, Show } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../../utils/withPagePermissionCheck.tsx";
import PermissionService from "../../../../services/PermissionService.ts";
import { BarController, BarElement, CategoryScale, Chart, Colors, LinearScale, Tooltip } from "chart.js";
import type { TranslationKey } from "../../../../state/i18n.tsx";
import { useTranslation } from "../../../../state/i18n.tsx";
import { groupBy, isNil, range } from "lodash-es";
import { CompactSingleSelect } from "../../../../components/menu/Select.tsx";
import { useCustomerState } from "../../../../state/customer.tsx";
import type { Usage } from "../../../../types.ts";
import ApiService from "../../../../services/ApiService.ts";
import FormatNumber from "../../../../components/formatting/FormatNumber.tsx";
import { SegmentGroup } from "@ark-ui/solid";
import { Icon } from "../../../../components/icon/Icon.tsx";
import { MONTH_ABBREVIATED_TRANSLATION_KEYS, MONTH_TRANSLATION_KEYS } from "../../../../utils/dateUtil.ts";
import { getTailwindProperty } from "../../../../utils/tailwindUtil.ts";
import { createWritableMemo } from "@solid-primitives/memo";
import { fallback, maxValue, minValue, number, object, optional, picklist, pipe, string, transform } from "valibot";
import { useTypedSearchParams } from "../../../../utils/solidRouterUtil.ts";
import { DEFAULT_DATE_FORMAT } from "../../../../components/formatting/FormatDate.tsx";
import SettingsPageHeader from "../components/SettingsPageHeader.tsx";
import { keyFromSelector } from "i18next";

Chart.register(Colors, BarController, BarElement, CategoryScale, LinearScale, Tooltip);

type Timeframe = "daily" | "monthly" | "billingPeriod";
type Grouping = "daily" | "monthly";
type BarChartData = {
  x: Date;
  y: number;
}[];

const createSearchParamSchema = (minYear: number, maxYear: number) =>
  object({
    timeframe: fallback(optional(picklist(["daily", "monthly", "billingPeriod"])), undefined),
    year: fallback(
      optional(
        pipe(
          string(),
          transform((input) => Number.parseInt(input, 10)),
          number(),
          minValue(minYear),
          maxValue(maxYear),
        ),
      ),
      undefined,
    ),
    month: fallback(
      optional(
        pipe(
          string(),
          transform((input) => Number.parseInt(input, 10)),
          number(),
          minValue(1),
          maxValue(12),
        ),
      ),
      undefined,
    ),
  });

const timeframeOptions: { key: Timeframe; translationKey: TranslationKey }[] = [
  { key: "daily", translationKey: keyFromSelector(($) => $.SETTINGS_USAGE_TIMEFRAME_DAILY) },
  { key: "monthly", translationKey: keyFromSelector(($) => $.SETTINGS_USAGE_TIMEFRAME_MONTHLY) },
  { key: "billingPeriod", translationKey: keyFromSelector(($) => $.SETTINGS_USAGE_TIMEFRAME_BILLING_PERIOD) },
];

const UsagePage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const { createdOn } = useCustomerState();

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();
  const customerSignupYear = createdOn.getUTCFullYear();

  const QueryParamsSchema = createSearchParamSchema(customerSignupYear, currentYear);
  const [searchParams, setSearchParams] = useTypedSearchParams(QueryParamsSchema);

  const [selectedTimeframe, setSelectedTimeframe] = createSignal<Timeframe>(searchParams.timeframe ?? "monthly");

  const years = range(currentYear, customerSignupYear - 1);
  const months = range(0, 12);
  const [selectedYear, setSelectedYear] = createWritableMemo(() => searchParams.year ?? currentYear);
  const [selectedMonth, setSelectedMonth] = createSignal(currentMonth);

  createEffect(() => {
    setSearchParams({ year: selectedYear(), month: selectedMonth() });
  });

  // TODO - DEV-7472 Replace with actual subscription data
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const billingPeriod = {
    interval: "monthly",
    from: oneMonthAgo,
    until: now,
  };

  const grouping = createMemo<Grouping>(() => {
    switch (selectedTimeframe()) {
      case "monthly":
        return "monthly";
      case "daily":
        return "daily";
      case "billingPeriod":
        return billingPeriod.interval === "monthly" ? "daily" : "monthly";
    }
  });

  const usagePeriod = createMemo(() => {
    const utcDate = (year: number, monthIndex: number, day: number) => new Date(Date.UTC(year, monthIndex, day));

    switch (selectedTimeframe()) {
      case "monthly": {
        const from = utcDate(selectedYear(), 0, 1);
        const until = utcDate(selectedYear() + 1, 0, 1);
        return { from, until };
      }
      case "daily": {
        const from = utcDate(selectedYear(), selectedMonth(), 1);
        const until = utcDate(selectedYear(), selectedMonth() + 1, 1);
        return { from, until };
      }
      case "billingPeriod":
        return billingPeriod;
    }
  });

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_BILLING_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_USAGE_LABEL)}
      />

      <div class="flex flex-col overflow-y-auto pt-8 pb-6">
        <SegmentGroup.Root
          value={selectedTimeframe()}
          orientation="horizontal"
          onValueChange={({ value }) => setSelectedTimeframe(value as Timeframe)}
        >
          <div class="rounded-sm border border-subtle p-6">
            <div class="flex justify-between gap-20">
              <div>
                <h4 class="text-header-lg text-primary">{t(($) => $.SETTINGS_USAGE_ANALYTICS_TITLE)}</h4>
                <span class="text-medium-regular text-secondary">{t(($) => $.SETTINGS_USAGE_ANALYTICS_SUB_TITLE)}</span>
              </div>

              <div>
                <div class="flex items-center gap-3">
                  <Show when={selectedTimeframe() === "daily"}>
                    <CompactSingleSelect
                      ariaLabel={t(($) => $.SETTINGS_USAGE_SELECT_MONTH_ARIA_LABEL)}
                      sameWidth
                      value={selectedMonth()}
                      options={months.map((month) => ({
                        variant: "text",
                        value: month,
                        label: t(MONTH_TRANSLATION_KEYS[month]),
                      }))}
                      onSelect={setSelectedMonth}
                    />
                  </Show>
                  <Show when={selectedTimeframe() === "daily" || selectedTimeframe() === "monthly"}>
                    <CompactSingleSelect
                      ariaLabel={t(($) => $.SETTINGS_USAGE_SELECT_YEAR_ARIA_LABEL)}
                      sameWidth
                      value={selectedYear()}
                      options={years.map((year) => ({
                        variant: "text",
                        value: year,
                        label: year.toString(),
                      }))}
                      onSelect={setSelectedYear}
                    />
                  </Show>
                  <div class="flex gap-x-2">
                    <div class="group relative isolate flex h-7.5 items-center justify-center rounded-sm border border-subtle bg-surface-extra-subtle">
                      <div class="grid grid-cols-3">
                        <For each={timeframeOptions}>
                          {(item) => (
                            <SegmentGroup.Item
                              value={item.key}
                              class="relative z-10 flex h-7.5 items-center justify-center rounded-sm bg-button-bg-tertiary-default p-2.5 text-base-medium text-button-text-tertiary-soft select-none checked:text-primary hover:text-button-text-tertiary-soft-hover data-focus-visible:outline-default"
                            >
                              <SegmentGroup.ItemText class="truncate text-button">
                                {t(item.translationKey)}
                              </SegmentGroup.ItemText>
                              <SegmentGroup.ItemControl />
                              <SegmentGroup.ItemHiddenInput />
                            </SegmentGroup.Item>
                          )}
                        </For>
                      </div>
                      <SegmentGroup.Indicator
                        class="-mx-px h-7.5 rounded-sm border border-default bg-surface-default"
                        style={{ width: "calc(var(--width) + 2px)", height: "var(--height)" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-10">
              <ChartContainer
                timeframe={selectedTimeframe()}
                grouping={grouping()}
                from={usagePeriod().from}
                until={usagePeriod().until}
              />
            </div>
          </div>
        </SegmentGroup.Root>
      </div>
    </div>
  );
};

type ChartContainerProps = {
  timeframe: Timeframe;
  grouping: Grouping;
  from: Date;
  until: Date;
};
const ChartContainer: Component<ChartContainerProps> = (props) => {
  const { t } = useTranslation();
  const query = createMemo(() => ApiService.getDailyUsage(props.from, props.until));

  // We need the previous data for displaying until new data has been fetched. Otherwise the complete UI will be updated
  // when fetching, since 'data' is empty during that time
  const [displayData, setDisplayData] = createSignal<Usage>();
  createEffect(() => {
    if (!query().isFetching && !isNil(query().data)) {
      setDisplayData(query().data);
    }
  });

  return (
    <div class="relative flex flex-col" aria-busy={query().isFetching}>
      <Show when={query().isFetching}>
        <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
          <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
          <span class="text-medium-regular text-primary">{t(($) => $.SETTINGS_USAGE_CHART_LOADING_TITLE)}</span>
        </div>
      </Show>

      <div class="flex justify-between">
        <div class="text-base-medium text-secondary">{t(($) => $.SETTINGS_USAGE_CHART_AXIS_Y_LABEL)}</div>
        <Show when={displayData()}>
          {(data) => (
            <dl class="flex gap-2">
              <dt class="text-base-medium text-secondary">{t(($) => $.SETTINGS_USAGE_CHART_TOTAL_SUBMISSIONS)}</dt>
              <dd class="text-base-medium text-primary">
                <FormatNumber value={data().total} />
              </dd>
            </dl>
          )}
        </Show>
      </div>

      <UsageChart timeframe={props.timeframe} grouping={props.grouping} data={displayData()?.data ?? []} />

      <div class="mt-2 text-right text-base-medium text-secondary">
        {props.grouping === "monthly"
          ? t(($) => $.SETTINGS_USAGE_CHART_AXIS_X_MONTHLY_LABEL)
          : t(($) => $.SETTINGS_USAGE_CHART_AXIS_X_DAILY_LABEL)}
      </div>
    </div>
  );
};

type UsageChartProps = {
  timeframe: Timeframe;
  grouping: Grouping;
  data: BarChartData;
};
const UsageChart: Component<UsageChartProps> = (props) => {
  const { currentLanguage } = useTranslation();
  let ref!: HTMLCanvasElement;

  const chartLabels = createMemo(() => generateChartLabels(props.data, props.timeframe, props.grouping));
  const tableLabels = createMemo(() => generateTableLabels(props.data, props.grouping));
  const data = createMemo(() => groupData(props.data, props.timeframe, props.grouping));

  onMount(() => {
    // DEV-7068 We have several CSS values that actually should be design tokens, we can use `getTailwindProperty` when
    // on Tailwind 4 to replace these and fetch them from Tailwind config.
    const chart = new Chart<"bar", number[]>(ref, {
      type: "bar",
      options: {
        locale: currentLanguage(),
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          y: {
            suggestedMin: 0,
            suggestedMax: 100,
            border: {
              display: false,
            },
            grid: {
              drawTicks: false,
            },
            ticks: {
              padding: 16,
              color: "#71717B", // text-secondary
              font: {
                // text-small-medium
                family: getTailwindProperty("font-family"), // DEV-7068 - Use '--default-font-family', when on Tailwind v4
                size: 12,
                lineHeight: "16px",
                weight: 500,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: "#71717B", // text-secondary
              font: {
                // text-small-medium
                family: getTailwindProperty("font-family"), // DEV-7068 - Use '--default-font-family', when on Tailwind v4
                size: 12,
                lineHeight: "16px",
                weight: 500,
              },
            },
          },
        },
        elements: {
          bar: {
            borderRadius: 4,
            backgroundColor: "#3F5D8B", // bg-accent-brand
          },
        },
        plugins: {
          tooltip: {
            caretSize: 0, // Remove the tooltip arrow
            displayColors: false, // Removes color box in front of value
            yAlign: "bottom", // Position the tooltip above the bar
            backgroundColor: "#FFFFFF", // bg-surface-default
            borderWidth: 1,
            borderColor: "#E4E4E7", // border-subtle
            bodyFont: {
              // text-small-medium
              family: getTailwindProperty("font-family"), // DEV-7068 - Use '--default-font-family', when on Tailwind v4
              size: 14,
              lineHeight: "20px",
              weight: 400,
            },
            callbacks: {
              title: () => "", // Remove title
              labelTextColor: () => "#3F3F46", // text-primary
            },
          },
        },
      },
      data: {
        labels: chartLabels(),
        datasets: [
          {
            data: [],
            maxBarThickness: 24,
          },
        ],
      },
    });

    onCleanup(() => {
      chart.destroy();
    });

    createEffect(
      on([() => chartLabels(), () => data()], ([labels, data]) => {
        chart.data.labels = labels;
        chart.data.datasets[0]!.data = data;
        chart.update();
      }),
    );
  });

  return (
    <div class="h-125 w-full">
      <canvas width="100%" ref={ref} aria-hidden="true" />

      <UsageTable grouping={props.grouping} labels={tableLabels()} data={data()} />
    </div>
  );
};

type UsageTableProps = {
  grouping: Grouping;
  labels: string[];
  data: number[];
};
const UsageTable: Component<UsageTableProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="sr-only">
      <table aria-label={t(($) => $.SETTINGS_USAGE_TABLE_ARIA_LABEL)}>
        <thead>
          <tr>
            <th>
              {props.grouping === "monthly"
                ? t(($) => $.SETTINGS_USAGE_TABLE_HEADER_MONTH)
                : t(($) => $.SETTINGS_USAGE_TABLE_HEADER_DAY)}
            </th>
            <th>{t(($) => $.SETTINGS_USAGE_TABLE_HEADER_AMOUNT_OF_SUBMISSIONS)}</th>
          </tr>
        </thead>
        <tbody>
          <For each={props.labels}>
            {(label, index) => (
              <tr>
                <td>{label}</td>
                <td>{props.data[index()]}</td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
};

const groupData = (data: BarChartData, timeframe: Timeframe, grouping: Grouping): number[] => {
  const groupedByMonth = () =>
    Object.entries(groupBy(data, ({ x }) => x.getUTCMonth())).map(([, y]) => y.reduce((acc, cur) => acc + cur.y, 0));
  const groupedByDay = () => data.map(({ y }) => y);

  switch (timeframe) {
    case "monthly":
      return groupedByMonth();
    case "daily":
      return data.map(({ y }) => y);
    case "billingPeriod":
      return grouping === "monthly" ? groupedByMonth() : groupedByDay();
  }
};

const generateChartLabels = (data: BarChartData, timeframe: Timeframe, grouping: Grouping): string[] => {
  const { t } = useTranslation();

  switch (timeframe) {
    case "monthly":
      return Object.values(MONTH_ABBREVIATED_TRANSLATION_KEYS).map((key) => t(key));
    case "daily":
      return data.map(({ x }) => x.getUTCDate().toString());
    case "billingPeriod":
      return grouping === "monthly"
        ? Object.values(MONTH_ABBREVIATED_TRANSLATION_KEYS).map((key) => t(key))
        : data.map(({ x }) => DEFAULT_DATE_FORMAT.format(x));
  }
};

const generateTableLabels = (data: BarChartData, grouping: Grouping): string[] => {
  const { t } = useTranslation();

  switch (grouping) {
    case "monthly":
      return Object.values(MONTH_TRANSLATION_KEYS).map((key) => t(key));
    case "daily":
      return data.map(({ x }) => DEFAULT_DATE_FORMAT.format(x));
  }
};

const ProtectedUsagePage = withPagePermissionCheck(UsagePage, PermissionService.canManageBilling);

export default ProtectedUsagePage;
